import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import Parser from 'html-react-parser';
import cn from 'classnames';
import { Card, Divider, Image, Segment, Message } from 'semantic-ui-react';
import { DataList } from '../';
import Svg from 'react-inlinesvg';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import {
  downloadIcon,
  logoAGIImage,
  chartIcon,
  videoIcon,
  docIcon,
  calcIcon
} from '../../images';
import { Badge, StreamModal, VideoPlayer } from '../';
import { withAnalytics } from '../../analytics';
import StyledProductCard from './ProductCard.style';
import { replaceAkeneoProdUrl } from '../../utils/helpers';

class ProductCard extends PureComponent {
  state = {
    isVideoModalOpen: false, // for video
    isTCOModalOpen: false // for TCO
  };

  labels = () => {
    const { intl } = this.props;
    return {
      tco: intl.formatMessage({
        id: 'cta.tco',
        defaultMessage: 'TCO Chart'
      }),
      video: intl.formatMessage({
        id: 'cta.watch_video',
        defaultMessage: 'Watch it Work!'
      }),
      tour: intl.formatMessage({
        id: 'cta.product_tour',
        defaultMessage: '3D Product Tour'
      }),
      specs: intl.formatMessage({
        id: 'cta.details_specs',
        defaultMessage: 'Details &amp; Specs'
      }),
      features: intl.formatMessage({
        id: 'cta.features_benefits',
        defaultMessage: 'Features &amp; Benefits'
      }),
      calculator: intl.formatMessage({
        id: 'cta.calculator',
        defaultMessage: 'Handling Length Calculator'
      })
    };
  };

  toggleTCOModal = isTCOModalOpen => {
    this.setState({ isTCOModalOpen });
    // analytics only for opening, not closing
    if (!isTCOModalOpen) {
      return;
    }
    this.handleExternalLinkClick('TCO', this.labels().tco);
  };

  toggleVideoModal = isVideoModalOpen => {
    this.setState({ isVideoModalOpen });
    // analytics only for opening, not closing
    if (!isVideoModalOpen) {
      return;
    }
    this.handleExternalLinkClick('Video', this.labels().video);
  };

  handleExternalLinkClick = (type, label, e, eventName) => {
    // to avoide conflict with mixpanel, manually open window
    if (e) {
      // declare here to avoide nullified object error
      // https://reactjs.org/docs/events.html#event-pooling
      const { target } = e;
      e.preventDefault();
      setTimeout(() => {
        window.open(target.href, target.target);
      }, 0);
    }
    this.handleProductLinkClick(
      eventName || 'Selling Tool Product Links Viewed',
      {
        'Link Type': type,
        'Clicked Link Text': label
      }
    );
  };

  handleProductLinkClick = (eventName, extra) => {
    const { product, analytics, isUpsell } = this.props;
    analytics.trackEvent(eventName, {
      'Dollar Amount': product.price,
      Tool: product.isSales ? 'Selling Tool' : 'Product Comparison Tool',
      isUpsellProduct: isUpsell,
      'Product Model': product.model,
      'Product Division': product.division,
      'Product ID': product.id,
      ...extra
    });
  };

  render() {
    const { product, dataList, priceList, editMode } = this.props;
    const linkLabels = this.labels();
    // signifier expiry check
    const expiry = product.productSignifierExpiryDate;
    const isSignifierValid =
      !expiry || (expiry && new Date(expiry).getTime() > new Date().getTime());

    const areProductLinksNotEmpty =
      product.tcoUrl ||
      product.videoUrl ||
      product.productTourUrl ||
      product.specUrl ||
      product.featureUrl ||
      product.lengthCalculatorUrl;

    const getDataList = data =>
      Object.keys(data)
        .filter(key => product[key])
        .map(key => ({
          title: data[key].title,
          data: data[key].format(product[key])
        }));

    const renderProductImage = () => {
      return (
        <figure className="productcard__image" data-focus>
          {product.productSignifier && isSignifierValid && (
            <Badge>{product.productSignifier}</Badge>
          )}
          {product.imageUrl ? (
            <Image src={replaceAkeneoProdUrl(product.imageUrl)} />
          ) : (
            <div className="productcard__image--placeholder">
              <Svg src={logoAGIImage} />
              <strong>{product.division}</strong>
            </div>
          )}
        </figure>
      );
    };

    const renderProductHeader = () => (
      <Fragment>
        {product.division && <Card.Meta>{product.division}</Card.Meta>}
        {product.name && (
          <h3 className="productcard__name" data-qa="header-product-card-name">
            {`${product.model ? product.model + ' ' : ''}${product.name}`}
            {(product.dealerPCode || product.dealerSalesCode) && (
              <Fragment>
                <br />
                {`${product.dealerPCode || ''}-${product.dealerSalesCode ||
                  ''}`}
              </Fragment>
            )}
          </h3>
        )}
      </Fragment>
    );

    const renderProductDescription = () => (
      <article className="productcard__description">
        <h4 className="productcard__description-title">
          <FormattedMessage
            id="subtitle.include"
            description="Description title (subtitle of product card)"
            defaultMessage="Include"
          />
        </h4>
        {Parser(DOMPurify.sanitize(product.description))}
      </article>
    );

    const renderProductCTAs = () => (
      <footer className="productcard__footer">
        <a
          href={product.specUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="productcard__link link--icon"
          onClick={e =>
            this.handleExternalLinkClick(
              'PDF',
              linkLabels.specs,
              e,
              'Comparison Tool Details & Spec Viewed'
            )
          }
        >
          <i className="icon">
            <Svg src={downloadIcon} />
          </i>
          {linkLabels.specs} (PDF)
        </a>
      </footer>
    );

    return (
      <StyledProductCard
        className={cn('productcard', editMode && 'productcard--focus')}
      >
        {renderProductImage()}
        {product.upsellBanner && (
          <Segment
            inverted
            color="yellow"
            className="productcard__badge productcard__badge--hang"
            compact
          >
            {product.upsellBanner}
          </Segment>
        )}

        <Card.Content>
          {renderProductHeader()}
          {product.description && <Divider />}
          {product.description && renderProductDescription()}
          {dataList && <DataList list={getDataList(dataList)} />}
          {product.isSales && priceList && (
            <DataList list={getDataList(priceList)} />
          )}

          {product.salesHighlights && <Divider />}

          {product.salesHighlights && (
            <div className="productcard__rte" data-focus>
              {/* allowing style attrs here in order to show the PIM's RTE bold/underline text. */}
              {Parser(
                DOMPurify.sanitize(product.salesHighlights, {
                  ALLOWED_ATTR: ['style']
                })
              )}
            </div>
          )}

          {product.isSales && areProductLinksNotEmpty && <Divider />}
          {product.isSales && areProductLinksNotEmpty && (
            <ul className="productcard__links" data-focus>
              {product.tcoUrl && (
                <li data-test="tco">
                  <a
                    className="productcard__link link--icon"
                    onClick={() => this.toggleTCOModal(true)}
                    tabIndex="0"
                  >
                    <i className="icon">
                      <Svg src={chartIcon} />
                    </i>
                    {linkLabels.tco}
                  </a>
                  <StreamModal
                    isOpen={this.state.isTCOModalOpen}
                    toggleModal={this.toggleTCOModal}
                  >
                    <Image src={product.tcoUrl} />
                  </StreamModal>
                </li>
              )}

              {product.videoUrl && (
                <li data-test="video">
                  <a
                    className="productcard__link link--icon"
                    onClick={() => this.toggleVideoModal(true)}
                    tabIndex="0"
                  >
                    <i className="icon">
                      <Svg src={videoIcon} />
                    </i>
                    {linkLabels.video}
                  </a>
                  <StreamModal
                    isOpen={this.state.isVideoModalOpen}
                    toggleModal={this.toggleVideoModal}
                    size="large"
                    basic
                  >
                    <VideoPlayer
                      url={product.videoUrl}
                      title={`${product.model ? product.model + ' ' : ''}${
                        product.name
                      }`}
                    />
                  </StreamModal>
                </li>
              )}

              {product.productTourUrl && (
                <li>
                  <a
                    href={product.productTourUrl}
                    target="agi"
                    className="productcard__link link--icon"
                    onClick={e =>
                      this.handleExternalLinkClick(
                        'External',
                        linkLabels.tour,
                        e
                      )
                    }
                  >
                    <i className="icon">
                      <Svg src={videoIcon} />
                    </i>
                    {linkLabels.tour}
                  </a>
                </li>
              )}

              {product.specUrl && (
                <li>
                  <a
                    href={product.specUrl}
                    target="agi"
                    className="productcard__link link--icon"
                    onClick={e =>
                      this.handleExternalLinkClick('PDF', linkLabels.specs, e)
                    }
                  >
                    <i className="icon">
                      <Svg src={downloadIcon} />
                    </i>
                    {linkLabels.specs} (PDF)
                  </a>
                </li>
              )}

              {product.featureUrl && (
                <li>
                  <a
                    href={product.featureUrl}
                    target="agi"
                    className="productcard__link link--icon"
                    data-qa="feature-benefits-url"
                    onClick={e =>
                      this.handleExternalLinkClick(
                        'Features & Benefits',
                        linkLabels.features,
                        e
                      )
                    }
                  >
                    <i className="icon">
                      <Svg src={docIcon} />
                    </i>
                    {linkLabels.features}
                  </a>
                </li>
              )}

              {product.lengthCalculatorUrl && (
                <li>
                  <a
                    href={product.lengthCalculatorUrl}
                    target="agi"
                    className="productcard__link link--icon"
                    onClick={e =>
                      this.handleExternalLinkClick(
                        'Calculator',
                        linkLabels.features,
                        e
                      )
                    }
                  >
                    <i className="icon">
                      <Svg src={calcIcon} />
                    </i>
                    {linkLabels.calculator}
                  </a>
                </li>
              )}
            </ul>
          )}

          {product.specBullets && <Divider />}
          {product.specBullets && (
            <div className="productcard__rte" data-focus>
              {Parser(
                DOMPurify.sanitize(product.specBullets, {
                  ALLOWED_ATTR: ['style']
                })
              )}
            </div>
          )}
        </Card.Content>

        {product.rebates && (
          <Message warning data-focus>
            {Parser(
              DOMPurify.sanitize(product.rebates, { ALLOWED_ATTR: ['style'] })
            )}
          </Message>
        )}
        {!product.isSales && product.specUrl && <Divider />}
        {!product.isSales && product.specUrl && renderProductCTAs()}
      </StyledProductCard>
    );
  }
}

ProductCard.propTypes = {
  intl: intlShape.isRequired,
  product: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  isUpsell: PropTypes.bool,
  dataList: PropTypes.shape({
    title: PropTypes.node,
    format: PropTypes.func
  }),
  priceList: PropTypes.shape({
    title: PropTypes.node,
    format: PropTypes.func
  }),
  editMode: PropTypes.bool
};

export default withAnalytics(injectIntl(ProductCard));
